import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import { NewPasswordFields } from "@/components/new-password-fields";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { t } from "@/i18n/pl";
import { ApiError } from "@/services/api-client";
import * as authService from "@/services/auth.service";

interface PasswordChangeFormProps {
  /** Login to sign back in with: the server revokes every session on a change. */
  username: string;
  /** "Obecne hasło (od administratora)" on first login, plain "Obecne hasło" later. */
  currentPasswordLabel: string;
  submitLabel: string;
  successMessage: string;
}

/**
 * Current / new / repeat — the one self-service password form, shared by the
 * first-login trap and the settings page. After the change the server revokes
 * all sessions, so we immediately sign back in with the new password: to the
 * user it feels like one smooth step.
 */
export function PasswordChangeForm({
  username,
  currentPasswordLabel,
  submitLabel,
  successMessage,
}: PasswordChangeFormProps) {
  const { signIn } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (newPassword.length < 12) {
      setError(t.auth.passwordMinLength);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t.auth.passwordsDontMatch);
      return;
    }
    if (newPassword === currentPassword) {
      setError(t.auth.passwordSameAsCurrent);
      return;
    }

    setSubmitting(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      await signIn(username, newPassword);
      toast.success(successMessage);
      // On the settings page the form stays mounted: never keep a password around.
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t.common.genericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)}>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="current-password">{currentPasswordLabel}</FieldLabel>
          <Input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            disabled={submitting}
          />
        </Field>
        <NewPasswordFields
          newPassword={newPassword}
          confirmPassword={confirmPassword}
          onNewPasswordChange={setNewPassword}
          onConfirmPasswordChange={setConfirmPassword}
          disabled={submitting}
          invalid={error !== null}
        />
        {error && (
          <FieldDescription role="alert" className="text-destructive">
            {error}
          </FieldDescription>
        )}
        <Button type="submit" className="h-11" disabled={submitting}>
          {submitting ? t.auth.settingPassword : submitLabel}
        </Button>
      </FieldGroup>
    </form>
  );
}
